import dayjs from 'dayjs'

/** Stała liczba kolumn w tabeli certyfikatów — niezależna od env (formularz szkoleń). */
const CERTIFICATES_LIST_HANDLOWIEC_PARTS = 3
const CERTIFICATES_LIST_HEAD_OF_SALES_PARTS = 2

const ROMAN_NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X']

export const ACTION_ENROL = 'enrol'
export const ACTION_DETAILS = 'details'
export const DETAIL_TAB_ENROLMENTS = 'enrolments'

const CERTIFICATION_CELL_PATHS = ['handlowiec', 'headOfSales']

export function collectCertificationAttempts(user) {
  if (!user) {
    return []
  }
  const attempts = []
  CERTIFICATION_CELL_PATHS.forEach(path => {
    const cells = user[path]
    if (!cells) {
      return
    }
    const cellList = Array.isArray(cells) ? cells : Object.values(cells)
    cellList.forEach(cell => {
      if (cell && Array.isArray(cell.attempts)) {
        attempts.push(...cell.attempts)
      }
    })
  })
  return attempts
}

export const PASSED_FILTER_PASSED = 'passed'
export const PASSED_FILTER_FAILED = 'failed'
export const PASSED_FILTER_NONE = 'none'

export function userHasPassedCertification(user) {
  return collectCertificationAttempts(user).some(attempt => attempt.state === 'PASSED')
}

/**
 * TAK  (passed) → has at least one PASSED
 * NIE  (failed) → has at least one FAILED and zero PASSED
 * BRAK (none)   → zero PASSED and zero FAILED (SCHEDULED and/or empty)
 */
export function userPassedFilterCategory(user) {
  const attempts = collectCertificationAttempts(user)
  if (attempts.some(attempt => attempt.state === 'PASSED')) {
    return PASSED_FILTER_PASSED
  }
  if (attempts.some(attempt => attempt.state === 'FAILED')) {
    return PASSED_FILTER_FAILED
  }
  return PASSED_FILTER_NONE
}

export function userMatchesCertificationDateRange(user, from, to) {
  if (!from && !to) {
    return true
  }
  // PASSED, FAILED and SCHEDULED (planned exam day) all count
  const datedAttempts = collectCertificationAttempts(user).filter(
    attempt => attempt.state === 'PASSED'
      || attempt.state === 'FAILED'
      || attempt.state === 'SCHEDULED',
  )
  return datedAttempts.some(attempt => {
    if (!attempt.date) {
      return false
    }
    const attemptDate = dayjs(attempt.date).startOf('day')
    if (from && attemptDate.isBefore(dayjs(from).startOf('day'))) {
      return false
    }
    if (to && attemptDate.isAfter(dayjs(to).startOf('day'))) {
      return false
    }
    return true
  })
}

export function buildCertificationStatusExportParams(filters) {
  const params = {}
  const {
    firstNameFilter,
    lastNameFilter,
    validUntilFrom,
    validUntilTo,
    roleFilter,
    certificationDateFrom,
    certificationDateTo,
    passedFilter,
  } = filters

  if (firstNameFilter) {
    params.firstName = firstNameFilter
  }
  if (lastNameFilter) {
    params.lastName = lastNameFilter
  }
  if (validUntilFrom) {
    params.validUntilFrom = validUntilFrom
  }
  if (validUntilTo) {
    params.validUntilTo = validUntilTo
  }
  if (roleFilter) {
    params.role = roleFilter
  }
  if (certificationDateFrom) {
    params.certificationDateFrom = certificationDateFrom
  }
  if (certificationDateTo) {
    params.certificationDateTo = certificationDateTo
  }
  if (
    passedFilter === PASSED_FILTER_PASSED
    || passedFilter === PASSED_FILTER_FAILED
    || passedFilter === PASSED_FILTER_NONE
  ) {
    params.passed = passedFilter
  }
  return params
}

export function filterCertificatesList(users, filters) {
  const {
    isManager,
    currentUserId,
    firstNameFilter,
    lastNameFilter,
    validUntilFrom,
    validUntilTo,
    roleFilter,
    certificationDateFrom,
    certificationDateTo,
    passedFilter,
  } = filters

  let list = Array.isArray(users) ? [...users] : []

  list = list.filter(user => (
    user.roles.includes('ROLE_EMPLOYEE') || user.roles.includes('ROLE_MANAGER')
  ))

  if (isManager) {
    list = list.filter(user => (
      user.id === currentUserId || user.roles.includes('ROLE_EMPLOYEE')
    ))
  }

  if (firstNameFilter) {
    const query = firstNameFilter.toLowerCase()
    list = list.filter(user => (user.firstName || '').toLowerCase().includes(query))
  }

  if (lastNameFilter) {
    const query = lastNameFilter.toLowerCase()
    list = list.filter(user => (user.lastName || '').toLowerCase().includes(query))
  }

  if (validUntilFrom || validUntilTo) {
    list = list.filter(user => {
      if (!user.latestValidUntil) {
        return false
      }
      const validUntil = dayjs(user.latestValidUntil).startOf('day')
      if (validUntilFrom && validUntil.isBefore(dayjs(validUntilFrom).startOf('day'))) {
        return false
      }
      if (validUntilTo && validUntil.isAfter(dayjs(validUntilTo).startOf('day'))) {
        return false
      }
      return true
    })
  }

  if (roleFilter) {
    list = list.filter(user => user.roles.includes(roleFilter))
  }

  if (certificationDateFrom || certificationDateTo) {
    list = list.filter(user => userMatchesCertificationDateRange(
      user,
      certificationDateFrom,
      certificationDateTo,
    ))
  }

  if (
    passedFilter === PASSED_FILTER_PASSED
    || passedFilter === PASSED_FILTER_FAILED
    || passedFilter === PASSED_FILTER_NONE
  ) {
    list = list.filter(user => userPassedFilterCategory(user) === passedFilter)
  }

  return list.sort((a, b) => {
    const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim()
    const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim()
    return nameA.localeCompare(nameB, 'pl', { sensitivity: 'base' })
  })
}

export function paginateList(list, perPage, currentPage) {
  const start = perPage * (currentPage - 1)
  return list.slice(start, start + perPage)
}

export function buildListMetadata(paginatedCount, perPage, currentPage, total) {
  return {
    from: perPage * (currentPage - 1) + (paginatedCount ? 1 : 0),
    to: perPage * (currentPage - 1) + paginatedCount,
    of: total,
  }
}

export function certificationLevelRomanNumeral(level) {
  const index = level - 1
  if (index >= 0 && index < ROMAN_NUMERALS.length) {
    return ROMAN_NUMERALS[index]
  }
  return String(level)
}

export function certificationLevelColumnLabel(level) {
  return `${certificationLevelRomanNumeral(level)} Certyfikacja`
}

export function buildCertificatesListCellKeys() {
  const keys = []
  for (let level = 1; level <= CERTIFICATES_LIST_HANDLOWIEC_PARTS; level += 1) {
    keys.push({
      key: `handlowiec-${level}`,
      path: 'handlowiec',
      level,
      isGroupStart: level === 1,
    })
  }
  for (let level = 1; level <= CERTIFICATES_LIST_HEAD_OF_SALES_PARTS; level += 1) {
    keys.push({
      key: `headOfSales-${level}`,
      path: 'headOfSales',
      level,
      isGroupStart: level === 1,
    })
  }
  return keys
}

export function certificatesListTableColumnCount() {
  return 2 + CERTIFICATES_LIST_HANDLOWIEC_PARTS + CERTIFICATES_LIST_HEAD_OF_SALES_PARTS
}

export function getCertificationCell(cells, level) {
  if (!cells) {
    return null
  }
  if (Array.isArray(cells)) {
    return cells.find(cell => cell && cell.level === level) || cells[level - 1] || null
  }
  return cells[level] || cells[level - 1] || null
}

export function formatCertificateValidUntil(user) {
  if (user.latestInfinite) {
    return 'Bezterminowo'
  }
  return user.latestValidUntil ? dayjs(user.latestValidUntil).format('YYYY-MM-DD') : '-'
}

export function buttonForCertificationAttempt(attempt, index) {
  const baseKey = `attempt-${attempt.enrolmentId || index}`

  if (attempt.state === 'SCHEDULED') {
    const time = attempt.date ? dayjs(attempt.date).format('HH:mm') : '-'
    return {
      key: baseKey,
      action: ACTION_DETAILS,
      detailTab: DETAIL_TAB_ENROLMENTS,
      enrolmentId: attempt.enrolmentId,
      certificationId: attempt.certificationId,
      canViewDetails: attempt.canViewDetails !== false,
      label: `TERMIN ${dayjs(attempt.date).format('YYYY-MM-DD')}`,
      variant: 'outline-secondary',
      tooltip: `Godzina certyfikacji: ${time}`,
    }
  }

  if (attempt.state === 'PASSED') {
    let validity = '-'
    if (attempt.infinite) {
      validity = 'Bezterminowo'
    } else if (attempt.validUntil) {
      validity = dayjs(attempt.validUntil).format('YYYY-MM-DD')
    }
    return {
      key: baseKey,
      action: ACTION_DETAILS,
      detailTab: DETAIL_TAB_ENROLMENTS,
      enrolmentId: attempt.enrolmentId,
      certificationId: attempt.certificationId,
      canViewDetails: attempt.canViewDetails !== false,
      label: 'ZALICZONY',
      variant: 'outline-success',
      tooltip: `Termin ważności: ${validity}`,
    }
  }

  const dateLabel = attempt.date ? dayjs(attempt.date).format('YYYY-MM-DD') : '-'
  return {
    key: baseKey,
    action: ACTION_DETAILS,
    detailTab: DETAIL_TAB_ENROLMENTS,
    enrolmentId: attempt.enrolmentId,
    certificationId: attempt.certificationId,
    canViewDetails: attempt.canViewDetails !== false,
    label: 'NIEZALICZONY',
    variant: 'outline-danger',
    tooltip: `Data certyfikacji: ${dateLabel}`,
  }
}

export function enrolCertificationButton(rowUser) {
  return {
    key: 'enrol',
    action: ACTION_ENROL,
    userId: rowUser.id,
    label: 'USTAL TERMIN',
    variant: 'outline-primary',
    tooltip: 'Ustal termin zapisu na certyfikację',
  }
}

export function cellButtonsForCertification(cell, rowUser, { isManager }) {
  if (!cell) {
    return []
  }

  const attempts = Array.isArray(cell.attempts) ? cell.attempts : []
  const buttons = attempts.map((attempt, index) => buttonForCertificationAttempt(attempt, index))

  if (cell.canEnrolNext === true && isManager) {
    buttons.push(enrolCertificationButton(rowUser))
  }

  return buttons
}
